import { Link } from "react-router-dom";
import BrandQR from "../components/BrandQR";

const LAST_UPDATED = "May 1, 2026";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      {/* Nav */}
      <nav className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-heading text-4xl font-semibold tracking-tight mb-2">
          Terms of Service
        </h1>
        <p className="text-sm text-zinc-500 mb-10">Last Updated: {LAST_UPDATED}</p>

        <div className="space-y-8 text-zinc-700 leading-relaxed">

          <p>
            Welcome to <strong>Growithevent</strong> ("we," "our," "us"). These Terms
            of Service ("Terms") govern your access to and use of our website:{" "}
            <a
              href="https://www.growithevent.com"
              className="text-zinc-900 underline underline-offset-2"
            >
              www.growithevent.com
            </a>
          </p>
          <p>
            By using our platform, you agree to comply with these Terms. If you do not
            agree, you must not use the platform.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              1. Description of Services
            </h2>
            <p className="mb-2">
              Growithevent provides a technology platform that enables:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Event creation and management</li>
              <li>Participant registration</li>
              <li>Payment collection</li>
              <li>QR-based entry and attendance tracking</li>
            </ul>
            <p className="mt-3">
              We act solely as a technology platform facilitating connections between
              event organizers and participants. We do not organize, manage, or endorse
              any event listed on the platform.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              2. Eligibility
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                The platform is currently intended primarily for users in{" "}
                <strong>India</strong>.
              </li>
              <li>Both individuals and businesses may use the platform.</li>
              <li>
                By using the platform, you represent that you are legally capable of
                entering into a binding agreement under applicable law.
              </li>
              <li>
                Users must be at least <strong>18 years of age</strong> to register or
                transact on the platform.
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              3. Account and Usage
            </h2>
            <p className="mb-2">Users agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide accurate and complete information</li>
              <li>
                Maintain the confidentiality of their account credentials
              </li>
              <li>Use the platform only for lawful purposes</li>
            </ul>
            <p className="mt-3">
              You are responsible for all activities conducted through your use of the
              platform. Notify us immediately at{" "}
              <a
                href="mailto:growithevent@gmail.com"
                className="text-zinc-900 underline underline-offset-2"
              >
                growithevent@gmail.com
              </a>{" "}
              if you suspect unauthorized access to your account.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              4. User Responsibilities
            </h2>
            <p className="mb-2">You agree <strong>NOT</strong> to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide false, misleading, or fraudulent information</li>
              <li>Attempt unauthorized access to the platform or its systems</li>
              <li>Engage in fraudulent payments or misuse payment systems</li>
              <li>Abuse QR code entry or attendance systems</li>
              <li>Use the platform for illegal or unauthorized events</li>
              <li>Create spam, misleading, or duplicate events</li>
              <li>Reverse-engineer or scrape any part of the platform</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              5. Organizer Responsibilities
            </h2>
            <p className="mb-2">
              If you create or manage events, you are <strong>solely responsible</strong> for:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Event quality and execution</li>
              <li>Venue, logistics, and services</li>
              <li>Accuracy of event details published on the platform</li>
              <li>Handling cancellations, refunds, and attendee communications</li>
              <li>Compliance with applicable local laws and regulations</li>
            </ul>
            <p className="mt-3">
              Growithevent does not take any responsibility for any aspect of the event
              itself.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              6. Payments and Fees
            </h2>

            <h3 className="font-semibold text-zinc-900 mb-2">
              6.1 Participant Payments
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Payments made by participants are processed through third-party payment
                providers (currently <strong>Cashfree</strong>).
              </li>
              <li>Growithevent does not provide refunds directly.</li>
              <li>
                Any refund requests must be addressed to the respective event organizer.
              </li>
            </ul>

            <h3 className="font-semibold text-zinc-900 mt-6 mb-2">
              6.2 Platform Fee
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                The total fee is 5% of each ticket: 2% is Razorpay&apos;s payment
                processing fee and 3% is Growithevent&apos;s platform fee.
              </li>
              <li>
                Ticket payments settle to the organizer&apos;s own Razorpay account on
                Razorpay&apos;s settlement schedule. Growithevent&apos;s 3% is invoiced to
                the organizer after the event.
              </li>
            </ul>

            <h3 className="font-semibold text-zinc-900 mt-6 mb-2">6.3 Payouts</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Payments are processed via the payment gateway and may be automatically
                split between organizers and the platform.
              </li>
              <li>
                Organizers receive funds directly through the configured payment system,
                subject to any settlement timelines of the payment provider.
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              7. Intellectual Property
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                The Growithevent platform — including its design, code, branding, and
                trademarks — is owned by us and protected under applicable intellectual
                property laws.
              </li>
              <li>Users retain ownership of their personal data.</li>
              <li>
                Event content (descriptions, images, details, etc.) belongs to the
                respective organizer.
              </li>
              <li>
                You may not copy, modify, redistribute, or create derivative works of
                our platform without our prior written permission.
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              8. Platform Role Disclaimer
            </h2>
            <p className="mb-2">
              Growithevent is a <strong>technology facilitator only</strong>. We:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Do NOT organize events</li>
              <li>Do NOT guarantee event quality or outcomes</li>
              <li>Do NOT control organizer behavior or event execution</li>
            </ul>
            <p className="mt-3">
              All transactions and interactions are strictly between participants and
              organizers.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              9. Limitation of Liability
            </h2>
            <p className="mb-2">
              To the fullest extent permitted by applicable law, Growithevent shall{" "}
              <strong>NOT</strong> be liable for:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Event cancellations, postponements, or delays</li>
              <li>Poor event experiences or unmet expectations</li>
              <li>Losses caused by organizer actions or inactions</li>
              <li>Payment disputes between users and organizers</li>
              <li>
                Any indirect, incidental, special, or consequential damages arising from
                your use of the platform
              </li>
            </ul>
            <p className="mt-3">
              Your use of the platform is entirely at your own risk.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              10. Suspension and Termination
            </h2>
            <p className="mb-2">We reserve the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Suspend or terminate accounts at any time</li>
              <li>Remove events or content that violate these Terms</li>
              <li>Block users who misuse the platform</li>
            </ul>
            <p className="mt-3">
              This may be done without prior notice in cases of misuse, fraud, or legal
              risk to the platform or its users.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              11. Changes to the Platform
            </h2>
            <p className="mb-2">We may, at any time and without prior notice:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Modify or discontinue features</li>
              <li>Update pricing or platform fees</li>
              <li>Change platform functionality or availability</li>
            </ul>
            <p className="mt-3">
              Continued use of the platform after any changes constitutes your
              acceptance of the updated Terms.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              12. Data and Privacy
            </h2>
            <p>
              Your use of the platform is also governed by our{" "}
              <Link
                to="/privacy-policy"
                className="text-zinc-900 underline underline-offset-2"
              >
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference. By using
              Growithevent, you consent to the data practices described therein.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              13. AI and Future Technologies
            </h2>
            <p className="mb-2">
              Currently, Growithevent does not use user data for AI training or
              automated decision-making.
            </p>
            <p className="mb-2">
              If AI features are introduced in the future:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Users acknowledge that AI outputs may not always be accurate</li>
              <li>
                Growithevent shall not be liable for decisions made based on
                AI-generated outputs
              </li>
              <li>This policy will be updated with appropriate notice</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              14. Prohibited Activities
            </h2>
            <p className="mb-2">You agree not to use the platform for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Illegal events or activities of any kind</li>
              <li>Fraudulent schemes or misrepresentation</li>
              <li>Spam, phishing, or deceptive practices</li>
              <li>Harassment or harmful conduct toward other users</li>
              <li>Circumventing platform fees or payment systems</li>
            </ul>
            <p className="mt-3">
              Violation of this section may result in immediate account termination and,
              where applicable, referral to law enforcement.
            </p>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              15. Governing Law and Jurisdiction
            </h2>
            <p className="mb-2">
              These Terms are governed by and construed in accordance with the laws of{" "}
              <strong>India</strong>, including applicable provisions of the Information
              Technology Act, 2000, and Consumer Protection Act, 2019.
            </p>
            <p>
              Any disputes arising out of or in connection with these Terms shall be
              subject to the exclusive jurisdiction of the courts in{" "}
              <strong>Gurugram, Haryana, India</strong>.
            </p>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 mb-4">
              16. Contact Information
            </h2>
            <p className="mb-3">
              For any questions, concerns, or disputes regarding these Terms, please
              contact us:
            </p>
            <ul className="space-y-1">
              <li>
                <span className="text-zinc-500">Email:</span>{" "}
                <a
                  href="mailto:growithevent@gmail.com"
                  className="text-zinc-900 underline underline-offset-2"
                >
                  growithevent@gmail.com
                </a>
              </li>
              <li>
                <span className="text-zinc-500">Address:</span> Gurugram, Haryana,
                India
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="max-w-4xl mx-auto px-6 py-10 text-sm text-zinc-500 border-t border-zinc-200 flex justify-between">
        <span>© Growithevent · MVP build</span>
        <Link to="/" className="hover:text-zinc-900 transition-colors">
          Back to Home
        </Link>
      </footer>
    </div>
  );
}
